import { useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Search, Heart, Plus, MessageSquare, UserCircle2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useUnread } from "@/context/UnreadContext";
import { useLang } from "@/context/LanguageContext";
import { inserierenZiel, roleDestination } from "@/lib/roleRedirect";
import useScrollChrome from "@/hooks/useScrollChrome";

// ---------------------------------------------------------------------------
// Mobile Bottom-Navigation (nur < md) — Gegenstück zur App-TabBar.
//
// Seit 29.07.2026 bewusst 1:1 der Look von mobile/components/GlassTabBar.tsx
// (Wunsch: „so sollte die am Handy-Web auch aussehen"). Übernommen von dort:
//  · BREITE Kapsel: füllt die Breite bis max. 360px statt der schmalen
//    intrinsischen Pille — ruhiger, und die Slots verteilen sich gleichmäßig.
//  · BESCHRIFTUNG unter jedem Icon (Usability-Befund aus der App: Icons allein
//    waren für weniger geübte Nutzer nicht eindeutig — „Was ist das Herz?").
//  · Aktiver Tab = ovale Blase HINTER dem Slot (8px breiter je Seite, damit
//    auch „Nachrichten" innerhalb liegt) mit heller Oberkante + Pop-Animation.
//  · „+" = runder Terrakotta-Kreis mit feiner heller Kante (kein Glow).
//  · Glas: Blur + dunkle Tönung + Glanzverlauf oben + innerer Ring — die
//    Web-Näherung an das native Chrome-Material; Farbwerte aus
//    mobile/theme/colors.ts (glass.*).
//
// KEIN Home-Slot — bewusst wie in der App (Entscheidung 29.07.): Zur
// Startseite führt das Avliana-Logo oben links (Navbar), die Leiste bleibt
// den fünf Arbeitsbereichen vorbehalten. Ein sechster Slot machte die Kapsel
// auf schmalen Geräten eng, ohne einen eigenen Bereich zu tragen.
//
// Ziele für Merkliste/Profil kommen aus roleDestination() — nie hartcodieren,
// sonst landen Makler/Bauträger im falschen Dashboard.
//
// Nachgezogen am 01.10.2026 („die Leiste am Handy sieht zurückgeblieben
// aus"): Die App hatte am 14.08. auf Instagram-Höhe abgespeckt — Slots 44
// statt 52, Polster 4 statt 8, FAB 44 statt 52, Kapsel dichter am unteren
// Rand —, das Web nicht. Die Kapsel war hier ~70 px hoch, in der App ~52.
// Dazu das Schrumpfen beim Runterscrollen (TabBarChrome.tsx): Scale .87,
// 8 px abtauchen, Deckkraft .55; zurück beim Hochscrollen oder wenn der
// Wisch ausläuft.
//
// Bewusst NICHT übernommen: die hauchdünne App-Tönung (.22). Die liegt dort
// auf Apples dunklem Chrome-Material; im Browser gibt es nur den Blur, und
// über hellem Inhalt wäre die Kapsel mit .22 grau-durchsichtig statt dunkel.
// ---------------------------------------------------------------------------

// Label-Schrift 10px wie in der App (und wie Apples native Tab-Bar) — als
// Inline-Stil, damit die mobile 12px-Untergrenze aus index.css hier NICHT
// greift: Sie gilt Fließtext; hier ist das ganze 52px-Feld das Tippziel, das
// Label nur die Stütze zum Icon.
const labelStyle = { fontSize: 10, lineHeight: "12px", letterSpacing: "0.1px", marginTop: 2 };

export default function BottomNav() {
  const { user } = useAuth();
  const { unread } = useUnread();
  const { t } = useLang();
  const { pathname, search } = useLocation();
  const navRef = useRef(null);
  // App-Werte: ab 40 px, 4 px runter = schrumpfen, 2 px hoch = zurück.
  const geschrumpft = useScrollChrome({ abY: 40, runter: 4, hoch: 2, stillstandMs: 700 });

  // Beim Zwei-Finger-Zoom hängt `position: fixed` am Layout-Viewport, nicht am
  // sichtbaren Ausschnitt — die Leiste wandert dann aus dem Bild und wird
  // mitvergrößert. Hier ziehen wir sie an den unteren Rand des SICHTBAREN
  // Bereichs zurück und rechnen den Zoom heraus, damit sie unverändert an
  // ihrem Platz steht.
  useEffect(() => {
    const vv = window.visualViewport;
    const nav = navRef.current;
    if (!vv || !nav) return undefined;

    const update = () => {
      // Ungezoomt bleibt die Leiste unangetastet — kein transform, damit im
      // Normalfall (99 % der Zeit) nichts vom Browser-Standard abweicht.
      if (vv.scale <= 1.01 && vv.offsetTop === 0 && vv.offsetLeft === 0) {
        nav.style.transform = "";
        return;
      }
      const unten = window.innerHeight - (vv.height + vv.offsetTop);
      nav.style.transformOrigin = "left bottom";
      nav.style.transform = `translate3d(${vv.offsetLeft}px, ${-unten}px, 0) scale(${1 / vv.scale})`;
    };

    update();
    vv.addEventListener("resize", update);
    vv.addEventListener("scroll", update);
    return () => {
      vv.removeEventListener("resize", update);
      vv.removeEventListener("scroll", update);
    };
  }, []);

  // Geschützte Bereiche führen ausgeloggt auf /login — aber MIT dem Tab im
  // Query (`?tab=messages`), damit die Blase am angetippten Tab kleben bleibt.
  // Ohne das leuchtete beim Ausgeloggten nur „Suche" (öffentlich); wer auf
  // Nachrichten oder Merkliste tippte, landete auf /login ohne jede Markierung
  // und hielt das für einen Fehler (Rückmeldung 30.07.). So folgt die Blase dem
  // Tab wie in der App — und übersteht einen Reload, weil der Tab im URL steht.
  const profileTo = user ? roleDestination(user) : "/login?tab=profile";
  const favoritesTo = user ? "/mein-bereich?tab=favorites" : "/login?tab=favorites";
  const messagesTo = user ? "/nachrichten" : "/login?tab=messages";

  // Aktiv-Zustand PRO TAB über die kanonische Sektion, nicht über das Linkziel.
  // Eingeloggt teilen sich Profil (/mein-bereich) und Merkliste
  // (/mein-bereich?tab=favorites) denselben Pfad — deshalb genau eine Sektion
  // pro Route. Ausgeloggt liefert der ?tab=-Query auf /login die Sektion.
  const params = new URLSearchParams(search);
  const loginTab = pathname === "/login" ? params.get("tab") : null;
  const onFavorites =
    (pathname === "/mein-bereich" && params.get("tab") === "favorites") || loginTab === "favorites";
  // „Inserieren"-Tab folgt der zentralen Rollenweiche: Gäste und Private
  // gehören auf /pakete (dort läuft der next=inserat-Flow), nur Gewerbliche
  // direkt in den Assistenten — vorher führte der Tab JEDEN dorthin und der
  // Wizard schickte Gäste erst nach mehreren Schritten wieder weg.
  const listTo = inserierenZiel(user);
  const activeByKey = {
    search: pathname.startsWith("/suche") || pathname.startsWith("/kaufen"),
    favorites: onFavorites,
    list: pathname.startsWith("/inserat-erstellen") || (listTo === "/pakete" && pathname.startsWith("/pakete")),
    messages: pathname.startsWith("/nachrichten") || loginTab === "messages",
    profile:
      loginTab === "profile" ||
      (!onFavorites &&
        ["/mein-bereich", "/makler-dashboard", "/developer-dashboard", "/admin"].some((p) => pathname.startsWith(p))),
  };

  const items = [
    { to: "/suche", Icon: Search, label: t("bnav.search"), key: "search" },
    { to: favoritesTo, Icon: Heart, label: t("bnav.favorites"), key: "favorites" },
    { to: listTo, Icon: Plus, label: t("bnav.list"), key: "list", fab: true },
    { to: messagesTo, Icon: MessageSquare, label: t("bnav.messages"), key: "messages" },
    { to: profileTo, Icon: UserCircle2, label: t("bnav.profile"), key: "profile" },
  ];

  return (
    // Vollbreiter, durchklickbarer Container (pointer-events-none) — nur die
    // Kapsel fängt Taps ab. px-4 = Mindest-Seitenabstand auf schmalen Geräten.
    <nav
      ref={navRef}
      data-testid="bottom-nav"
      aria-label={t("bnav.aria")}
      className="md:hidden fixed inset-x-0 bottom-0 z-50 pointer-events-none px-6 pb-[max(calc(env(safe-area-inset-bottom)-2px),8px)]"
    >
      <div
        data-geschrumpft={geschrumpft ? "ja" : undefined}
        className={`pointer-events-auto relative mx-auto w-full max-w-[360px] overflow-hidden rounded-full bg-[rgba(22,21,20,0.70)] backdrop-blur-xl backdrop-saturate-150 border border-white/[0.22] shadow-[0_18px_28px_-10px_rgba(0,0,0,0.30)] origin-bottom transition-[transform,opacity] duration-200 ease-out ${
          geschrumpft ? "scale-[0.87] translate-y-2 opacity-[0.55]" : ""
        }`}
      >
        {/* Gewölbter Glanz oben + Hauch Abdunklung unten (GlassTabBar: sheenTop). */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 bg-gradient-to-b from-white/20 via-white/5 to-black/5"
        />
        {/* Innerer Ring (GlassTabBar: glass.ring). */}
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 rounded-full ring-1 ring-inset ring-white/10" />
        <ul className="relative flex items-center justify-between px-3 py-1">
          {items.map(({ to, Icon, label, key, fab }) => {
            const active = !!activeByKey[key];
            if (fab) {
              return (
                <li key={key} className="flex-none">
                  <Link
                    to={to}
                    data-testid={`bnav-${key}`}
                    aria-label={label}
                    className="grid w-11 h-11 place-items-center rounded-full bg-terracotta text-white border border-white/25 hover:bg-terracotta-hover active:bg-terracotta-hover transition-transform active:scale-[0.94]"
                  >
                    <Plus className="w-6 h-6" strokeWidth={2.2} />
                  </Link>
                </li>
              );
            }
            // Ungelesen-Badge am Nachrichten-Symbol: die Bottom-Nav ist am
            // Handy der einzige immer sichtbare Ort dafür.
            const badge = key === "messages" && unread > 0;
            return (
              // flex-1 + Deckel 52px: die Slots verteilen sich wie in der App
              // (space-between); auf sehr schmalen Geräten schrumpfen sie gleichmäßig,
              // statt aus der Kapsel zu laufen. Tipphöhe 44px wie in der App
              // (Apples Mindest-Touchziel).
              <li key={key} className="min-w-0 max-w-[52px] flex-1">
                <Link
                  to={to}
                  data-testid={`bnav-${key}`}
                  aria-label={badge ? `${label} (${unread})` : label}
                  aria-current={active ? "page" : undefined}
                  className={`relative flex h-11 w-full flex-col items-center justify-center transition-colors duration-200 active:scale-95 ${
                    active ? "text-white" : "text-white/[0.72] hover:text-white"
                  }`}
                >
                  {/* Stadium-Oval hinter dem Slot (GlassTabBar: slotBubble) —
                      8px breiter je Seite, damit lange Labels innerhalb liegen.
                      Ohne helle Oberkante: die zarte Specular-Linie las sich als
                      1px-Fehler statt als Glas-Kante (Rückmeldung 29.07.). */}
                  {active && (
                    <span
                      aria-hidden="true"
                      className="pointer-events-none absolute -inset-x-2 inset-y-0 rounded-full bg-white/[0.18]"
                    />
                  )}
                  {/* key erzwingt den Neustart der Pop-Animation beim Aktivieren
                      (GlassTabBar: federnde Scale-Animation). */}
                  <span
                    key={active ? "an" : "aus"}
                    className="relative flex flex-col items-center"
                    style={active ? { animation: "bnav-pop 0.3s cubic-bezier(0.34,1.56,0.64,1)" } : undefined}
                  >
                    <Icon className="w-5 h-5" strokeWidth={active ? 2.4 : 2} />
                    <span className={`whitespace-nowrap ${active ? "font-semibold" : "font-medium"}`} style={labelStyle}>
                      {label}
                    </span>
                  </span>
                  {badge && (
                    <span
                      data-testid="bnav-unread-badge"
                      className="absolute top-[5px] end-[6px] grid min-w-4 h-4 place-items-center rounded-full bg-terracotta px-1 font-bold leading-none text-white"
                      style={{ fontSize: 10 }}
                    >
                      {unread > 9 ? "9+" : unread}
                    </span>
                  )}
                </Link>
              </li>
            );
          })}
        </ul>
      </div>
    </nav>
  );
}
